// Mirror angle convention (screen coordinates: +x east, +y south):
//   angleDeg is the rotation of the mirror surface from the horizontal axis, measured
//   clockwise (the same direction Phaser's setRotation rotates a horizontal bar).
//   surface direction = (cos θ, sin θ); normal = (sin θ, -cos θ) is perpendicular to it.
//   0°   → horizontal surface "─"; normal = (0, -1) faces north.
//   45°  → "\" diagonal (NW-to-SE); east beam reflects to south (slides down the slope).
//   90°  → vertical surface "|"; normal = (1, 0) faces east; east beam reflects west.
//   135° → "/" diagonal (SW-to-NE); east beam reflects to north.
//   315° → also "/" (same physical line as 135°, opposite parameterisation); same reflection.
//   Angles are continuous — drag is free, wheel/tap snap.
//
// Mirrors are double-sided: the reflection formula `dir − 2(dir·n)n` produces the same
// outgoing direction whether the normal is n or −n, so the angle parameter only needs to
// define the surface line, not a "front" vs "back".
//
// Edge-on hits: if the beam direction is within PARALLEL_ANGLE_DEG of the mirror surface
// AND the beam's line passes within MIRROR_EDGE_THICKNESS of the mirror's centre line, the
// beam is blocked at the nearer endpoint. Without a wide enough threshold, near-parallel
// beams get tiny mathematical deflections that look indistinguishable from passthrough.

import type { Color, LevelSpec } from '../level/schema';
import { rayHitSegment, reflect, type Ray, type Segment, type Vec2 } from './geometry';

export type Primary = 'R' | 'G' | 'B';
export type ColorSet = ReadonlySet<Primary>;

const ALL_PRIMARIES: ReadonlyArray<Primary> = ['R', 'G', 'B'];

/** Decompose a source/beam color into its primary components. */
export function colorToSet(color: Color): ColorSet {
  if (color === 'W') return new Set(ALL_PRIMARIES);
  return new Set([color]);
}

/** True iff every element of `required` is present in `incident`. */
export function meetsRequirement(incident: ColorSet, required: ReadonlyArray<Primary>): boolean {
  for (const r of required) if (!incident.has(r)) return false;
  return true;
}

function unionInto(target: Set<Primary>, source: ColorSet): Set<Primary> {
  for (const x of source) target.add(x);
  return target;
}

const MAX_BOUNCES = 16;
// Beams within this many degrees of parallel to a mirror surface are treated as edge-on.
// 5° gives the player a clear "tilt off-axis to start reflecting" feel and avoids the
// near-zero-deflection visual glitch where a barely-tilted mirror appears to pass beams.
const PARALLEL_ANGLE_DEG = 5;
const PARALLEL_CROSS_EPS = Math.sin((PARALLEL_ANGLE_DEG * Math.PI) / 180);
// Perpendicular distance (in cell units) within which an edge-on beam blocks at the mirror.
const MIRROR_EDGE_THICKNESS = 0.05;
// EPS used by rayHitSegment; matched here for forward-direction checks.
const T_EPS = 1e-6;

export interface MirrorState {
  slotIndex: number;
  angleDeg: number; // multiple of 15
}

export interface PrismState {
  slotIndex: number;
  inputDir: 'N' | 'S' | 'E' | 'W';
}

export interface BeamSegment {
  a: Vec2;
  b: Vec2;
  colors: ColorSet;
}
export interface BeamReflection {
  point: Vec2;
  beamDir: Vec2;
  reflectedDir: Vec2;
}
export interface TraceResult {
  segments: BeamSegment[];
  litSwitchColors: Map<string, ColorSet>;
  litSwitchIds: Set<string>;
  reflections: BeamReflection[];
}

interface ResolvedMirror {
  pos: Vec2;
  segment: Segment;
  normal: Vec2;
}

interface PendingBeam {
  origin: Vec2;
  dir: Vec2;
  colors: ColorSet;
}

export function trace(
  level: LevelSpec,
  mirrors: MirrorState[],
  prisms: PrismState[] = [],
): TraceResult {
  const segments: BeamSegment[] = [];
  const litSwitchColors = new Map<string, Set<Primary>>();
  const reflections: BeamReflection[] = [];

  const resolved = mirrors.map((m) => resolveMirror(level, m));
  const resolvedPrisms = prisms.map((p) => resolvePrism(level, p));
  const wallSegments = wallsToSegments(level);
  const gridBounds = boundsSegments(level);

  const queue: PendingBeam[] = level.lightSources.map((src) => ({
    origin: cellCenter(src.pos),
    dir: directionVec(src.direction),
    colors: colorToSet(src.color),
  }));

  let totalBounces = 0;

  while (queue.length > 0 && totalBounces < MAX_BOUNCES) {
    const beam = queue.shift()!;
    const ray: Ray = { origin: beam.origin, dir: beam.dir };

    const candidates: Array<{
      t: number;
      kind: 'wall' | 'edge' | 'mirror' | 'switch' | 'prism-input' | 'prism-passthrough';
      data?: ResolvedMirror | ResolvedPrism | string;
    }> = [];

    for (const seg of wallSegments) {
      const hit = rayHitSegment(ray, seg);
      if (hit) candidates.push({ t: hit.t, kind: 'wall' });
    }
    for (const seg of gridBounds) {
      const hit = rayHitSegment(ray, seg);
      if (hit) candidates.push({ t: hit.t, kind: 'edge' });
    }
    for (const m of resolved) {
      const hit = mirrorHit(ray, m);
      if (!hit) continue;
      if (hit.kind === 'reflect') candidates.push({ t: hit.t, kind: 'mirror', data: m });
      else candidates.push({ t: hit.t, kind: 'wall' });
    }
    for (const p of resolvedPrisms) {
      const hit = prismHit(ray, p);
      if (!hit) continue;
      candidates.push({ t: hit.t, kind: hit.kind, data: p });
    }
    for (const s of level.switches) {
      const hit = rayHitSegment(ray, switchSegment(s.pos, beam.dir));
      if (hit) candidates.push({ t: hit.t, kind: 'switch', data: s.id });
    }

    candidates.sort((x, y) => x.t - y.t);

    let stopped = false;
    let nextBeam: PendingBeam | null = null;

    for (const c of candidates) {
      if (c.kind === 'switch') {
        const id = c.data as string;
        const prev = litSwitchColors.get(id) ?? new Set<Primary>();
        litSwitchColors.set(id, unionInto(prev, beam.colors));
        continue;
      }

      const hitPoint = {
        x: ray.origin.x + ray.dir.x * c.t,
        y: ray.origin.y + ray.dir.y * c.t,
      };
      segments.push({ a: ray.origin, b: hitPoint, colors: beam.colors });

      if (c.kind === 'mirror') {
        const m = c.data as ResolvedMirror;
        const reflectedDir = reflect(ray.dir, m.normal);
        reflections.push({
          point: hitPoint,
          beamDir: { x: ray.dir.x, y: ray.dir.y },
          reflectedDir,
        });
        nextBeam = { origin: hitPoint, dir: reflectedDir, colors: beam.colors };
        totalBounces++;
      } else if (c.kind === 'prism-input') {
        const p = c.data as ResolvedPrism;
        for (const role of ALL_PRIMARIES) {
          if (!beam.colors.has(role)) continue;
          const outDir = prismOutputDir(p.inputDir, role);
          // Emit from the OUTPUT face center (just outside the prism cell), so
          // each split beam travels along the prism's centre column/row and can
          // hit cell-centre-aligned switches downstream.
          queue.push({
            origin: { x: p.pos.x + outDir.x * 0.5, y: p.pos.y + outDir.y * 0.5 },
            dir: outDir,
            colors: new Set<Primary>([role]),
          });
        }
        totalBounces++;
        // The current beam terminates at the prism input face.
      } else if (c.kind === 'prism-passthrough') {
        const p = c.data as ResolvedPrism;
        const exitPoint = farSidePoint(hitPoint, beam.dir, p.pos);
        // Replace the just-pushed segment with one that spans the whole prism cell.
        segments[segments.length - 1] = {
          a: ray.origin,
          b: exitPoint,
          colors: beam.colors,
        };
        nextBeam = { origin: exitPoint, dir: beam.dir, colors: beam.colors };
        // Passthrough does NOT consume a bounce.
      } else {
        // wall or edge
        stopped = true;
      }
      break;
    }

    if (stopped || !nextBeam) continue;
    queue.push(nextBeam);
  }

  // Derive litSwitchIds from each switch's requires (subset semantics).
  const litSwitchIds = new Set<string>();
  for (const s of level.switches) {
    const incident = litSwitchColors.get(s.id);
    if (!incident) continue;
    if (s.requires.length === 0) {
      litSwitchIds.add(s.id);
      continue;
    }
    if (meetsRequirement(incident, s.requires)) litSwitchIds.add(s.id);
  }

  return {
    segments,
    litSwitchColors: litSwitchColors as Map<string, ColorSet>,
    litSwitchIds,
    reflections,
  };
}

function cellCenter(p: { x: number; y: number }): Vec2 {
  return { x: p.x + 0.5, y: p.y + 0.5 };
}

function directionVec(d: 'N' | 'S' | 'E' | 'W'): Vec2 {
  switch (d) {
    case 'N':
      return { x: 0, y: -1 };
    case 'S':
      return { x: 0, y: 1 };
    case 'E':
      return { x: 1, y: 0 };
    case 'W':
      return { x: -1, y: 0 };
  }
}

function boundsSegments(level: LevelSpec): Segment[] {
  const w = level.grid.width;
  const h = level.grid.height;
  return [
    { a: { x: 0, y: 0 }, b: { x: w, y: 0 } },
    { a: { x: w, y: 0 }, b: { x: w, y: h } },
    { a: { x: w, y: h }, b: { x: 0, y: h } },
    { a: { x: 0, y: h }, b: { x: 0, y: 0 } },
  ];
}

function wallsToSegments(level: LevelSpec): Segment[] {
  // Each wall cell becomes 4 line segments around its perimeter.
  const segs: Segment[] = [];
  for (const w of level.walls ?? []) {
    const x0 = w.x,
      y0 = w.y,
      x1 = w.x + 1,
      y1 = w.y + 1;
    segs.push({ a: { x: x0, y: y0 }, b: { x: x1, y: y0 } });
    segs.push({ a: { x: x1, y: y0 }, b: { x: x1, y: y1 } });
    segs.push({ a: { x: x1, y: y1 }, b: { x: x0, y: y1 } });
    segs.push({ a: { x: x0, y: y1 }, b: { x: x0, y: y0 } });
  }
  return segs;
}

function resolveMirror(level: LevelSpec, m: MirrorState): ResolvedMirror {
  const slot = level.slots[m.slotIndex];
  if (!slot) throw new Error(`mirror slot ${m.slotIndex} missing`);
  const rad = (m.angleDeg * Math.PI) / 180;
  const center = cellCenter(slot);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Surface segment is 1 cell long, centred on the cell. Beams that aren't parallel to the
  // surface and pass through the cell will cross this segment for the vast majority of
  // angles; the parallel-blocking threshold (5°) catches the rest.
  const half = 0.5;
  const segment: Segment = {
    a: { x: center.x - cos * half, y: center.y - sin * half },
    b: { x: center.x + cos * half, y: center.y + sin * half },
  };
  // True surface normal: perpendicular to (cos, sin). (Earlier code used (-sin, -cos),
  // which is anti-parallel to the surface, not perpendicular — that produced reflections
  // computed across the normal line instead of the surface line, i.e. as if the mirror
  // were rotated 90°. With the correct normal, the visual "\" mirror reflects east → south
  // and "/" reflects east → north, matching physical intuition.)
  const normal: Vec2 = { x: sin, y: -cos };
  return { pos: center, segment, normal };
}

/**
 * Hit-test a ray against a mirror, handling the edge-on parallel case.
 *
 * Returns a `'reflect'` hit when the ray crosses the mirror segment normally, or a
 * `'block'` hit when the ray is parallel to the mirror surface AND its line passes within
 * MIRROR_EDGE_THICKNESS of the mirror — in which case the beam is treated as colliding
 * with the nearer end of the mirror (otherwise an infinitely-thin segment would let a
 * perfectly-aligned beam pass through visually).
 */
function mirrorHit(
  ray: Ray,
  mirror: ResolvedMirror,
): { t: number; kind: 'reflect' | 'block' } | null {
  const reflectHit = rayHitSegment(ray, mirror.segment);
  if (reflectHit) return { t: reflectHit.t, kind: 'reflect' };

  const segVecX = mirror.segment.b.x - mirror.segment.a.x;
  const segVecY = mirror.segment.b.y - mirror.segment.a.y;
  const segLen = Math.hypot(segVecX, segVecY);
  if (segLen < T_EPS) return null;

  // Cross product of unit ray direction and segment direction = sin(angle between them).
  const cross = (ray.dir.x * segVecY - ray.dir.y * segVecX) / segLen;
  if (Math.abs(cross) > PARALLEL_CROSS_EPS) return null; // not parallel

  // Parallel; check perpendicular distance from ray.origin to the mirror's line.
  const dxA = mirror.segment.a.x - ray.origin.x;
  const dyA = mirror.segment.a.y - ray.origin.y;
  const perpDist = Math.abs(dxA * segVecY - dyA * segVecX) / segLen;
  if (perpDist > MIRROR_EDGE_THICKNESS) return null;

  // Edge-on hit: project both endpoints onto the ray direction and pick the smallest
  // forward-facing parameter — that's where the beam first reaches the mirror.
  const tA = dxA * ray.dir.x + dyA * ray.dir.y;
  const tB =
    (mirror.segment.b.x - ray.origin.x) * ray.dir.x +
    (mirror.segment.b.y - ray.origin.y) * ray.dir.y;
  let t = Infinity;
  if (tA >= T_EPS && tA < t) t = tA;
  if (tB >= T_EPS && tB < t) t = tB;
  if (t === Infinity) return null;

  return { t, kind: 'block' };
}

/**
 * Build a hit-test segment for a switch at the given cell. The segment is
 * perpendicular to the beam direction, centred on the switch cell, with radius
 * `r` (in cell units). This works for any beam direction including diagonals
 * (mirrors snap to 1° increments, so diagonal beams are normal). The hit
 * detection is purely mathematical: a beam intersects the perpendicular segment
 * iff its line passes within `r` cell-distance of the switch centre.
 *
 * One UX caveat for very oblique angles: the perpendicular hit segment crosses
 * the cell at an angle, so a beam barely glancing the cell corner can register
 * a hit even though it visually appears to pass empty space outside the switch
 * sprite. In practice this hasn't been a problem at the angles slice-1 and
 * slice-2 levels actually use.
 */
function switchSegment(pos: { x: number; y: number }, dir: Vec2): Segment {
  // A small segment perpendicular to the ray direction, at the switch cell's center.
  const center = cellCenter(pos);
  const r = 0.45;
  // Perpendicular to dir
  const px = -dir.y,
    py = dir.x;
  return {
    a: { x: center.x - px * r, y: center.y - py * r },
    b: { x: center.x + px * r, y: center.y + py * r },
  };
}

interface ResolvedPrism {
  pos: Vec2;
  inputDir: Vec2;
}

function resolvePrism(level: LevelSpec, p: PrismState): ResolvedPrism {
  const slot = level.slots[p.slotIndex];
  if (!slot) throw new Error(`prism slot ${p.slotIndex} missing`);
  return { pos: cellCenter(slot), inputDir: directionVec(p.inputDir) };
}

/**
 * Hit-test a ray against a 1-cell prism centred at p.pos. Returns 'prism-input'
 * if the ray enters the input face, 'prism-passthrough' if it crosses any other
 * face, null otherwise.
 */
function prismHit(
  ray: Ray,
  p: ResolvedPrism,
): { t: number; kind: 'prism-input' | 'prism-passthrough' } | null {
  const cx = p.pos.x;
  const cy = p.pos.y;
  const half = 0.5;
  const faces: Array<{ outward: Vec2; seg: Segment }> = [
    {
      outward: { x: 0, y: -1 },
      seg: { a: { x: cx - half, y: cy - half }, b: { x: cx + half, y: cy - half } },
    },
    {
      outward: { x: 0, y: 1 },
      seg: { a: { x: cx - half, y: cy + half }, b: { x: cx + half, y: cy + half } },
    },
    {
      outward: { x: -1, y: 0 },
      seg: { a: { x: cx - half, y: cy - half }, b: { x: cx - half, y: cy + half } },
    },
    {
      outward: { x: 1, y: 0 },
      seg: { a: { x: cx + half, y: cy - half }, b: { x: cx + half, y: cy + half } },
    },
  ];

  let best: { t: number; outward: Vec2 } | null = null;
  for (const f of faces) {
    const hit = rayHitSegment(ray, f.seg);
    if (!hit) continue;
    if (best === null || hit.t < best.t) best = { t: hit.t, outward: f.outward };
  }
  if (!best) return null;

  // The closest face the ray hits from outside the cell is, by construction, the
  // face the beam entered through. Compare its outward direction to the prism's
  // declared input face: if they match, this is an input-face hit (split);
  // otherwise it's a non-input face (passthrough).
  const sameDir = (a: Vec2, b: Vec2): boolean =>
    Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6;
  const isInputFace = sameDir(best.outward, p.inputDir);
  return { t: best.t, kind: isInputFace ? 'prism-input' : 'prism-passthrough' };
}

/**
 * For a prism with input outward direction d_in, return the outgoing direction
 * of the given role color (R/G/B):
 *   G out -d_in (opposite input)
 *   R out 90° CCW of d_in (screen coords with +y down: (x,y) → (y,-x))
 *   B out 90° CW of d_in (screen coords with +y down: (x,y) → (-y,x))
 */
export function prismOutputDir(d_in: Vec2, role: Primary): Vec2 {
  if (role === 'G') return { x: -d_in.x, y: -d_in.y };
  if (role === 'R') return { x: d_in.y, y: -d_in.x };
  return { x: -d_in.y, y: d_in.x };
}

/**
 * Given an entry point on a prism cell centred at `center` and a ray direction,
 * return the point where the ray exits the opposite side of the cell.
 */
function farSidePoint(entry: Vec2, dir: Vec2, center: Vec2): Vec2 {
  const half = 0.5;
  const tAxis = (axisDir: number, axisCoord: number, axisCenter: number): number => {
    if (Math.abs(axisDir) < 1e-9) return Infinity;
    const target = axisDir > 0 ? axisCenter + half : axisCenter - half;
    return (target - axisCoord) / axisDir;
  };
  const tx = tAxis(dir.x, entry.x, center.x);
  const ty = tAxis(dir.y, entry.y, center.y);
  const t = Math.min(tx, ty);
  if (!isFinite(t) || t <= 0) return entry;
  return { x: entry.x + dir.x * t, y: entry.y + dir.y * t };
}
